import { IFetchQuery, IFetchQueryAction, IUnknownRecord } from "@cd2/skeem-client"
import { useEffect, useState } from "react"

type IQuery = IFetchQuery | IFetchQueryAction

interface IuseFetchOptions {
  // if true then the query will not be executed
  skip?: boolean
  // variables to pass to the query function
  variables?: Record<string, any>
}

type IuseFetchReturn<T extends {} = IUnknownRecord> = {
  query: IQuery
  variables?: Record<string, any>
  networkStatus: NetworkStatus
  refetch(variables?: any): void
} & (
  | {
      data?: T
      isLoading: true
      error: Record<string, any>
    }
  | {
      data: T
      isLoading: false
      error: undefined
    }
  | {
      data?: T
      isLoading: false
      error: Record<string, any>
    }
)

export enum NetworkStatus {
  // the query has not been run and is currently in flight
  loading = 1,
  // the query was successful with no errors
  ready = 2,
  // errors occurred during the request
  error = 3,
  // reload caused by calling refetch
  refetch = 4,
}

export function useFetch(
  query: (variables: any) => IFetchQueryAction,
  options?: IuseFetchOptions,
): IuseFetchReturn<number>
export function useFetch(
  query: IFetchQueryAction,
  options?: Omit<IuseFetchOptions, "variables">,
): IuseFetchReturn<number>
export function useFetch<T>(
  query: (variables: any) => IFetchQuery,
  options?: IuseFetchOptions,
): IuseFetchReturn<(T & { id: string })[]>
export function useFetch<T>(
  query: IFetchQuery,
  options?: Omit<IuseFetchOptions, "variables">,
): IuseFetchReturn<(T & { id: string })[]>
export function useFetch(
  query:
    | IFetchQuery
    | IFetchQueryAction
    | ((variables: Record<string, any>) => IFetchQuery | IFetchQueryAction),
  options: IuseFetchOptions = {},
): any {
  if (options.variables && typeof query !== "function") {
    throw new Error("Cannot use the variables option unless the query given is a function")
  }

  const skeem = window.skeem
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>(NetworkStatus.loading)

  const [results, setResults] = useState({
    data: undefined as any,
    error: undefined as any,
    query: undefined as any,
    variables: {} as any,
  })

  useEffect(() => {
    if (options.skip) {
      return
    }
    setNetworkStatus(NetworkStatus.loading)
    runQuery(options.variables)
  }, [])

  async function runQuery(variables: any) {
    const finalQuery: any = typeof query === "function" ? query(variables ?? {}) : query
    try {
      const data = await skeem.fetch(finalQuery)
      setResults({
        data,
        error: undefined,
        query: finalQuery,
        variables,
      })
      setNetworkStatus(NetworkStatus.ready)
    } catch (error) {
      setResults({
        data: undefined,
        error,
        query: finalQuery,
        variables,
      })
      setNetworkStatus(NetworkStatus.error)
    }
  }

  function refetch(variables?: any) {
    if (variables && typeof query !== "function") {
      throw new Error("Cannot use the variables option unless the query given is a function")
    }
    setNetworkStatus(NetworkStatus.refetch)
    return runQuery(variables ?? options.variables)
  }

  const isLoading =
    networkStatus === NetworkStatus.loading || networkStatus === NetworkStatus.refetch

  return {
    refetch,
    networkStatus,
    isLoading,
    ...results,
  }
}
